/** @jsxImportSource @emotion/react */
import Toast from 'components/atoms/Toast'
import { confirm } from 'components/molecules/Confirmation'
import React from 'react'
import { Button } from 'react-bootstrap'
import {
  AttachmentOwner,
  useDeleteBillingAttachmentMutation,
  useGetAttachmentUploadUrlMutation,
  useGetBillingAttachmentUrlMutation,
  useRecordBillingAttachmentMutation,
} from 'store/services/billingAttachments'
import { BillingAttachmentDto } from 'store/types'
import { formatEasternDateTime } from 'helpers/dates'

// No size limit and no type restriction, matching order documents: the file
// goes straight to S3 without passing through the API, so nothing here needs a
// ceiling to protect the server.
interface Props {
  owner: AttachmentOwner
  attachments: BillingAttachmentDto[]
  onChange?: () => void
  // Whether the signed-in user may add and remove documents here. Charges and
  // payments are gated on different permissions, so the caller decides.
  canModify: boolean
}

const formatTimestamp = (value: string | Date | null | undefined) => formatEasternDateTime(value, '')

const uploaderOf = (attachment: BillingAttachmentDto): string => {
  const name = [attachment.uploaderFirstName, attachment.uploaderLastName]
    .filter(Boolean)
    .join(' ')
    .trim()

  if (name) return name
  if (attachment.uploaderEmail) return attachment.uploaderEmail

  return 'Unknown'
}

// Anything with a preview worth opening in a tab; everything else downloads.
const isViewable = (contentType: string) =>
  contentType === 'application/pdf' || contentType.startsWith('image/')

export const BillingAttachments: React.FC<Props> = ({
  owner,
  attachments,
  onChange,
  canModify,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = React.useState(false)
  const [getUploadUrl] = useGetAttachmentUploadUrlMutation()
  const [recordAttachment] = useRecordBillingAttachmentMutation()
  const [deleteAttachment] = useDeleteBillingAttachmentMutation()
  const [getUrl] = useGetBillingAttachmentUrlMutation()

  const handleSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    // Reset immediately so selecting the same file twice in a row still fires.
    event.target.value = ''

    if (!file) return

    setIsUploading(true)

    try {
      const { attachmentId, url } = await getUploadUrl({ owner, fileName: file.name }).unwrap()

      // Straight to S3, outside the API and outside RTK Query — a pre-signed
      // PUT takes the raw body and must not carry our auth headers.
      const response = await fetch(url, {
        method: 'PUT',
        body: file,
        headers: file.type ? { 'Content-Type': file.type } : undefined,
      })

      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`)
      }

      await recordAttachment({
        owner,
        attachmentId,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
      }).unwrap()

      onChange?.()
      Toast({ type: 'success', label: 'Attachment uploaded' })
    } catch (error: any) {
      Toast({
        type: 'error',
        label: error?.data?.message || error?.message || 'Failed to upload attachment',
      })
    } finally {
      setIsUploading(false)
    }
  }

  const handleOpen = async (attachment: BillingAttachmentDto) => {
    try {
      const { url } = await getUrl({
        owner,
        attachmentId: attachment.id,
        preview: isViewable(attachment.contentType),
      }).unwrap()

      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (error: any) {
      Toast({ type: 'error', label: error?.data?.message || 'Failed to open attachment' })
    }
  }

  const handleDelete = async (attachment: BillingAttachmentDto) => {
    try {
      await confirm('', {
        title: 'Remove this attachment?',
        description: `"${attachment.fileName}" will no longer be listed here.`,
        yesAction: 'Remove',
      })
    } catch {
      return
    }

    try {
      await deleteAttachment({ owner, attachmentId: attachment.id }).unwrap()
      onChange?.()
      Toast({ type: 'success', label: 'Attachment removed' })
    } catch (error: any) {
      Toast({ type: 'error', label: error?.data?.message || 'Failed to remove attachment' })
    }
  }

  return (
    <div>
      {attachments.length === 0 && (
        <div className="text-muted font-italic mb-2">No attachments yet</div>
      )}

      {attachments.map((attachment) => (
        <div
          key={attachment.id}
          className="d-flex align-items-center"
          css={{ gap: '0.5rem', padding: '0.5rem 0', borderBottom: '1px solid #e9ecef' }}>
          <i className="fas fa-paperclip text-muted" />
          <div css={{ flex: 1, minWidth: 0 }}>
            <Button
              variant="link"
              css={{ padding: 0, textAlign: 'left', overflowWrap: 'anywhere' }}
              onClick={() => handleOpen(attachment)}>
              {attachment.fileName}
            </Button>
            <div className="text-muted small">
              {uploaderOf(attachment)} · {formatTimestamp(attachment.createdAt)}
            </div>
          </div>
          {canModify && (
            <Button
              variant="outline-danger"
              size="sm"
              css={{ whiteSpace: 'nowrap' }}
              onClick={() => handleDelete(attachment)}>
              Remove
            </Button>
          )}
        </div>
      ))}

      {canModify && (
        <div className="mt-2">
          <input
            ref={inputRef}
            type="file"
            css={{ display: 'none' }}
            onChange={handleSelected}
          />
          <Button
            variant="outline-primary"
            size="sm"
            disabled={isUploading}
            onClick={() => inputRef.current?.click()}>
            <i className="fas fa-paperclip mr-2" />
            {isUploading ? 'Uploading...' : 'Attach file'}
          </Button>
        </div>
      )}
    </div>
  )
}
